import { type ReactElement, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"

import HistoryIcon from "@mui/icons-material/History"
import { Grid } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import { SelectWithSearch } from "../../../../components/form/SelectWithSearch/SelectWithSearch"
import { useCreateActualDecisionMutation } from "../../hooks/useCreateActualDecisionMutation"
import type { ActualDecisionSelectorProps } from "../../interfaces"
import { HistoryLog } from "./HistoryLog"

import styles from "../RequestBreakDown/RequestBreakDownTable.module.scss"

/**
 * Dropdown for setting the actual decision label, with history popover.
 */
export function ActualDecisionSelector(props: ActualDecisionSelectorProps): ReactElement {
  const { projectId, current, labels, hasNextLabelsPage, fetchNextLabelsPage, setLabelsSearchValue } = props
  const { t } = useTranslation("decisions", { keyPrefix: "actualDecisionSelector" })
  const createActualDecisionMutation = useCreateActualDecisionMutation()
  const [historyAnchor, setHistoryAnchor] = useState<HTMLButtonElement | null>(null)

  // Infinite scroll for label options
  const { ref: menuItemRef } = useInView({
    onChange(inView) {
      if (inView && hasNextLabelsPage) fetchNextLabelsPage()
    },
  })

  // Adjusted label options with separator
  const adjustedOptions = useMemo(() => {
    const adjusted = labels?.map((label, i) => ({
      text: label.name,
      value: label.uuid,
      item: (
        <Typography variant="a" variantColor={i === 0 ? 2 : 1}>
          {label.name}
        </Typography>
      ),
      disabled: false,
      listItemStyles: i === 0 ? { marginBottom: "0px" } : undefined,
    }))
    if (labels?.[0]?.name === t("notProvided") && labels.length > 1) {
      adjusted?.splice(1, 0, {
        text: "",
        value: "",
        item: <div className={styles.separator}></div>,
        disabled: true,
        listItemStyles: undefined,
      })
    }
    return adjusted
  }, [labels, t])

  return (
    <Grid container width={160} gap={1} wrap="nowrap" alignItems="center">
      <SelectWithSearch
        inputValue={current.actual_decision ?? t("notProvided")}
        optionsList={adjustedOptions || []}
        onSelectMenuItem={({ value }) => {
          createActualDecisionMutation.mutate({
            project_uuid: projectId as string,
            decisions: [
              {
                prediction: current.uuid,
                label: value || null,
              },
            ],
          })
        }}
        menuTriggerProps={{
          disabled: createActualDecisionMutation.isLoading,
          fullWidth: true,
          hideDescription: true,
        }}
        searchInputChangeHandler={(e) => setLabelsSearchValue(e.target.value.toString())}
        onMenuClose={() => setLabelsSearchValue(undefined)}
        lastMenuItemRef={menuItemRef}
      />

      <Grid item>
        <Button variant="ghost" onClick={(e) => setHistoryAnchor(e?.target as HTMLButtonElement)}>
          <HistoryIcon fontSize="small" />
        </Button>
        {historyAnchor && (
          <HistoryLog
            id={current.uuid}
            isOpen={Boolean(historyAnchor)}
            anchorEl={historyAnchor}
            onClose={() => setHistoryAnchor(null)}
            predictionId={current.uuid}
            variant="actual-decision"
            enabled={Boolean(historyAnchor)}
          />
        )}
      </Grid>
    </Grid>
  )
}
